import {
  getOneTrustDomainScriptId,
  getOneTrustDomainSdkStubSrc,
  ONETRUST_GENERIC_SDK_STUB_SRC,
  type Env,
} from '@fff-web/constants';
import { setOneTrustDataSubjectParams } from './set-onetrust-data-subject';

const appendSdkStub = (src: string, domainScriptId: string, onError?: () => void) => {
  const OTTag = document.createElement('script');
  OTTag.src = src;
  OTTag.type = 'text/javascript';
  OTTag.charset = 'UTF-8';
  OTTag.setAttribute('data-domain-script', domainScriptId);
  if (onError) OTTag.onerror = onError;
  document.head.appendChild(OTTag);
  return OTTag;
};

/**
 * Appends the domain-scoped stub; if it fails to load, removes its tag (the stub locates itself by
 * the first `otSDKStub` tag) and appends the generic stub once.
 */
const appendSdkStubWithFallback = (domainScriptId: string) => {
  const domainStub = appendSdkStub(
    getOneTrustDomainSdkStubSrc(domainScriptId),
    domainScriptId,
    () => {
      domainStub.remove();
      appendSdkStub(ONETRUST_GENERIC_SDK_STUB_SRC, domainScriptId);
    },
  );
};

/** Upper bound on the whole pre-init step (user context + flag + token) before the banner loads. */
const OVERALL_BUDGET_MS = 2000;

/** Resolves when `promise` settles or `ms` elapses (whichever first); never rejects. Runs `onTimeout` on timeout and always clears the timer. */
const raceWithTimeout = (
  promise: Promise<void>,
  ms: number,
  onTimeout: () => void,
): Promise<void> => {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<void>((resolve) => {
    timer = setTimeout(() => {
      onTimeout();
      resolve();
    }, ms);
  });
  return Promise.race([promise.catch(() => undefined), timeout]).finally(() => clearTimeout(timer));
};

export const isTargetingAllowed = () => window?.OnetrustActiveGroups?.includes('C0004');

/**
 * Claims OneTrust loading for this app. Set synchronously, before any await, so it is in place by the
 * time GTM-6N92's "Cookie Banner Script" tag runs (gtm.js executes in a later task) and the tag skips
 * injecting the generic stub.
 */
const markOneTrustSelfManaged = () => {
  window.__FFF_ONETRUST_SELF_MANAGED = true;
};

export const loadCookieConsentScript = async () => {
  markOneTrustSelfManaged();
  if (window?.OneTrust !== undefined) return;

  const controller = new AbortController();
  await raceWithTimeout(setOneTrustDataSubjectParams(controller.signal), OVERALL_BUDGET_MS, () =>
    controller.abort(),
  );

  if (typeof document !== 'undefined') {
    appendSdkStubWithFallback(getOneTrustDomainScriptId(process.env.REACT_APP_ENVIRONMENT as Env));

    if (!window.OptanonWrapper) {
      const optWrapperScript = document.createElement('script');
      optWrapperScript.type = 'text/javascript';
      optWrapperScript.text = 'function OptanonWrapper() {}';
      document.head.appendChild(optWrapperScript);
    }
  }
};
